<template>
  <section class="container">
    <div class="card">
      <transition :duration="400" name="component-fade" mode="out-in">
        <div v-if="!showOtpForm" key="enroll-auth">
          <div class="card-image conf" />
          <div class="card-title">Configure Authenticator app</div>
          <transition :duration="400" name="component-fade" mode="out-in">
            <div class="card-content" v-if="!showCode" key="qr-content">
              <p>Scan this barcode with an authenticator app (Authy, Google Authenticator, Microsoft Authenticator, LastPass, etc):</p>
              <div class="barcode-area">
                <qrcode :value="qrdata" :options="{ width: 170, margin: 0 }"></qrcode>
                <button class="text-button" v-on:click="showCode = true">Can't scan the barcode?</button>
              </div>
            </div>
            <div class="card-content" v-else key="code-content">
              <p>
                <strong>Can’t scan the barcode?</strong> You can enter this key on the Authenticator app to complete the configuration:
              </p>
              <div class="qr-code-area">
                <span class="code">{{ secret }}</span>
                <v-popover offset>
                  <button
                    class="text-button clipboard"
                    type="button"
                    v-clipboard:copy="secret"
                    v-clipboard:success="() => codeCopied = true"
                    v-clipboard:error="() => codeCopied = false"
                  >copy to clipboard</button>
                  <template slot="popover">
                    <span>{{ codeCopied ? 'Copied!' : 'Error'}}</span>
                  </template>
                </v-popover>
              </div>
            </div>
          </transition>
          <transition :duration="400" name="component-fade" mode="out-in">
            <div class="card-actions" v-if="!showCode" key="qr-action">
              <button class="store-button secondary" v-on:click="cancelEnroll">Cancel</button>
              <button class="store-button" v-on:click="showOtpForm = true">Next</button>
            </div>
            <div class="card-actions" v-else key="code-action">
              <button class="store-button secondary" v-on:click="showCode = false">Back</button>
              <button class="store-button" v-on:click="showOtpForm = true">Next</button>
            </div>
          </transition>
        </div>
        <div v-else key="otp-auth">
          <div class="card-image otp" />
          <form id="code-form" v-on:submit.prevent="onSubmit" novalidate>
            <div class="card-title">Configure Authenticator app</div>
            <div class="card-content">
              <p class="otp-desc">Enter the 6 digit code you see in the app.</p>
              <fieldset class="form-item">
                <div class="form-label">
                  <label for="code">Enter Code</label>
                  <div
                    class="error-message"
                    v-if="!$v.code.required && $v.code.$dirty"
                  >Field required</div>
                  <div class="error-message" v-else-if="otpError && $v.code.$dirty">{{ otpError }}</div>
                  <div class="error-message" v-else-if="timeout">{{ timeout }}</div>
                </div>
                <input
                  type="text"
                  name="code"
                  v-bind:class="{ error: ((!$v.code.required || otpError || timeout) && $v.code.$dirty) }"
                  v-model.trim.lazy="$v.code.$model"
                  maxlength="6"
                  placeholder="Enter code"
                  aria-label="Enter code"
                />
              </fieldset>
            </div>
            <div class="card-actions">
              <button
                type="button"
                class="store-button secondary"
                v-on:click="$v.$reset(); showOtpForm = false"
              >Back</button>
              <button
                type="submit"
                class="store-button"
                :class="{'busy': loading}"
                :disabled="loading"
              >
                <span class="ardu-store-button-text">Verify</span>
                <div class="spinner-container">
                  <div class="spinner"></div>
                </div>
              </button>
            </div>
          </form>
        </div>
      </transition>
    </div>
  </section>
</template>

<script>
import { required } from 'vuelidate/lib/validators';

export default {
  name: 'ConfigureAuthApp',
  props: ['qrdata', 'secret', 'mfaToken', 'state'],
  data() {
    return {
      showCode: false,
      codeCopied: false,
      showOtpForm: false,
      code: null,
      otpError: null,
      loading: null,
      timeout: null
    };
  },
  validations: {
    code: {
      required
    }
  },
  methods: {
    async onSubmit() {
      this.$v.$reset();
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          this.loading = true;
          const response = await fetch(`${process.env.VUE_APP_MFA_URL}oauth/token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.mfaToken}`
            },
            body: JSON.stringify({
              client_id: process.env.VUE_APP_AUTH0_CLIENT_ID,
              grant_type: 'http://auth0.com/oauth/grant-type/mfa-otp',
              otp: this.code,
              mfa_token: this.mfaToken
            })
          });
          if (!response.ok) {
            this.loading = false;
            const result = await response.json();
            if (result.error_description && result.error_description.indexOf('otp') > -1) {
              this.otpError = 'Wrong code, please try again';
              return;
            }
            if (result.error_description && result.error_description.indexOf('mfa_token is expired') > -1) {
              this.timeout = 'Timeout error, please refresh the page';
              return;
            }
            throw new Error(result.error_description || 'Something went wrong, please retry later');
          } else {
            const token = await window.auth.getAccessToken();
            const userApiResponse = await fetch(`${process.env.VUE_APP_API_URL}/users/v1/users/byID/me/mfa`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({})
            });
            if (!userApiResponse.ok) {
              throw new Error('Something went wrong, please refresh the page');
            }
            this.loading = false;
            this.$emit('enrolled')
          }
        } catch (e) {
          this.$toasted.show(e)
          this.loading = false;
        }
      }
    },
    cancelEnroll() {
      if (this.state) {
        window.location.replace(`https://${process.env.VUE_APP_AUTH0_DOMAIN}/continue?state=${this.state}`);
      } else {
        window.location.replace(process.env.VUE_APP_ID_PROFILE_URL);
      }
    }
  }
};
</script>

<style lang="scss">
.card-image {
  &.conf {
    background-image: url("../assets/configure-auth.svg");
  }

  &.otp {
    background-image: url("../assets/otp.svg");
  }
}

.barcode-area {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 40px;

  button {
    margin-top: 10px;
  }
}

.qr-code-area {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  border: 1px solid $fog;
  margin-top: 40px;

  .code {
    margin-bottom: 15px;
    color: $gold;
    font-size: 22px;
    word-break: break-all;
    line-height: 1.2;
  }
}

.otp-desc {
  margin-bottom: 60px;
}

.text-button {
  @extend %text-button;

  text-transform: unset !important;

  &.clipboard {
    text-transform: uppercase !important;
  }
}

.tooltip {
  display: block !important;
  z-index: 10000;

  .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: $teal2;
    z-index: 1;
  }

  &[x-placement^="top"] {
    margin-bottom: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^="bottom"] {
    margin-top: 5px;

    .tooltip-arrow {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^="right"] {
    margin-left: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[x-placement^="left"] {
    margin-right: 5px;

    .tooltip-arrow {
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[aria-hidden="true"] {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s, visibility 0.15s;
  }

  &[aria-hidden="false"] {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.15s;
  }

  &.popover {
    $color: $teal2;

    .popover-inner {
      background: $teal2;
      color: $white;
      border-radius: 16px;
      font-size: 16px;
      padding: 5px 10px 5px;
    }

    .popover-arrow {
      border-color: $color;
    }
  }
}
</style>
